import type { InspectionTemplateBody } from '@ayumia/contracts';
import { resolveProperty } from '@ayumia/component-runtime';
import type { Answers, Entry, Section } from './form-targets';

/**
 * Avertissements d'une fiche (inspection-template-body 1.5.0 ; addendum d'ADR-126 du 2026-10-18),
 * côté web : doublon d'une valeur dans une série (`duplicates.in_series`) et avertissements déclarés
 * (`warnings[].when`, sur `$answers` du groupe et `$form` de la fiche). Le même calcul que le serveur
 * (`formWarnings` du Core) et que la tablette (`form_rules.dart`), éprouvé par le jeu partagé
 * `form-warnings.cases.json`. Un avertissement ne bloque jamais l'envoi. Les doublons « déjà
 * enregistré » (`duplicates.existing`) demandent une lecture : le serveur les rend par
 * `PreviewFormWarnings` et dans le résultat de la complétion.
 */

export interface FormWarning {
  field: string;
  code: 'FORM_DUPLICATE_IN_SERIES' | 'FORM_DUPLICATE_EXISTING' | 'FORM_WARNING' | 'FORM_VALIDATION_WARNING';
  message: string;
  warning_id?: string;
  /** form-warnings 1.1.0 : validation déclarée « avertir » non respectée. */
  rule?: 'range' | 'length' | 'pattern';
  line?: number;
  matches?: Array<{ object_id: string; label?: string; on?: string }>;
}

type Text = Readonly<Record<string, string>> | undefined;

/** Libellé localisé, en français d'abord ; `{clé}` remplacé par sa valeur (comme `warningText` du Core). */
export function warningText(text: Text, fallback: string, values: Readonly<Record<string, unknown>>): string {
  const template = text?.['fr'] ?? text?.['en'] ?? fallback;
  return template.replace(/\{([a-z_]+)\}/g, (whole, key: string) => (values[key] === undefined || values[key] === null ? whole : String(values[key])));
}

/** Valeur comparée pour un doublon : chaîne rognée, nombre, booléen ; vide ou autre : pas de comparaison. */
export function duplicateKey(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function truthy(value: unknown): boolean {
  return !(value === false || value === null || value === undefined || value === 0);
}

function holdsOn(expression: unknown, answers: Answers, form: Answers): boolean {
  try {
    return truthy(resolveProperty({ expression }, { answers, form }));
  } catch {
    return false;
  }
}

type Rule = 'range' | 'length' | 'pattern';
const RULES: readonly Rule[] = ['range', 'length', 'pattern'];

/**
 * Validations déclarées « avertir » (inspection-template-body 1.13.0, ADR-146 addendum (23)) non respectées :
 * même calcul et mêmes messages que `validationWarningsOf` du Core et que `form_rules.dart`. Une valeur vide
 * n'est pas vérifiée ; les bornes pour un nombre, la longueur et le format pour un texte.
 */
export function validationWarnings(entry: Entry, value: unknown): Array<{ rule: Rule; message: string }> {
  const severity = (entry as { validation_severity?: Partial<Record<Rule, string>> }).validation_severity;
  const warned = severity === undefined ? [] : RULES.filter((rule) => severity[rule] === 'warn');
  if (warned.length === 0 || value === undefined || value === null || value === '') return [];
  const validation = (entry.field.validation ?? {}) as { min?: number; max?: number; min_length?: number; max_length?: number; pattern?: string };
  const out: Array<{ rule: Rule; message: string }> = [];
  for (const rule of warned) {
    if (rule === 'range' && typeof value === 'number') {
      const { min, max } = validation;
      if ((min !== undefined && value < min) || (max !== undefined && value > max)) out.push({ rule, message: min !== undefined && max !== undefined ? `Valeur attendue entre ${min} et ${max}` : min !== undefined ? `Valeur attendue d’au moins ${min}` : `Valeur attendue d’au plus ${String(max)}` });
    } else if (rule === 'length' && typeof value === 'string') {
      const { min_length: low, max_length: high } = validation;
      if ((low !== undefined && value.length < low) || (high !== undefined && value.length > high)) out.push({ rule, message: low !== undefined && high !== undefined ? `Entre ${low} et ${high} caractères attendus` : low !== undefined ? `Au moins ${low} caractères attendus` : `Au plus ${String(high)} caractères attendus` });
    } else if (rule === 'pattern' && typeof value === 'string' && typeof validation.pattern === 'string') {
      let matches = true;
      try {
        matches = new RegExp(validation.pattern, 'u').test(value);
      } catch {
        matches = true;
      }
      if (!matches) out.push({ rule, message: 'Format non respecté' });
    }
  }
  return out;
}

type Duplicates = { in_series?: boolean; in_series_message?: Text };
type Declared = { warning_id: string; when: unknown; message: Text };

/** Avertissements calculables sur la fiche seule ; même ordre que le serveur (fiche, puis chaque ligne). */
export function collectWarnings(template: InspectionTemplateBody, answers: Answers): FormWarning[] {
  const warnings: FormWarning[] = [];
  const groups: Array<{ section?: Section; entries: readonly Entry[]; values: Answers; prefix: string; index?: number }> = [];
  const plain: Entry[] = [];
  for (const section of template.sections) {
    if (section.repeat !== undefined || section.series !== undefined) {
      const rows = Array.isArray(answers[section.section_id]) ? (answers[section.section_id] as Answers[]) : [];
      rows.forEach((row, index) => groups.push({ section, entries: section.fields, values: Object.fromEntries(Object.entries(row ?? {}).filter(([key]) => key !== 'exit')), prefix: `answers.${section.section_id}.${index}`, index }));
      continue;
    }
    plain.push(...section.fields);
  }
  if (plain.length > 0) groups.unshift({ entries: plain, values: answers, prefix: 'answers' });
  for (const group of groups) {
    for (const entry of group.entries) {
      const id = entry.field.field_id;
      const duplicates = (entry as { duplicates?: Duplicates }).duplicates;
      if (duplicates?.in_series === true && group.section?.series !== undefined && group.index !== undefined) {
        const key = duplicateKey(group.values[id]);
        const rows = answers[group.section.section_id] as Answers[];
        const other = key === null ? -1 : rows.findIndex((row, index) => index !== group.index && duplicateKey(row?.[id]) === key);
        if (other >= 0) warnings.push({ field: `${group.prefix}.${id}`, code: 'FORM_DUPLICATE_IN_SERIES', line: other + 1, message: warningText(duplicates.in_series_message, 'Valeur {value} déjà saisie ligne {line}', { value: key, line: other + 1 }) });
      }
      for (const warning of validationWarnings(entry, group.values[id])) warnings.push({ field: `${group.prefix}.${id}`, code: 'FORM_VALIDATION_WARNING', rule: warning.rule, message: warning.message });
      for (const declared of ((entry as { warnings?: Declared[] }).warnings ?? [])) {
        if (holdsOn(declared.when, group.values, answers)) warnings.push({ field: `${group.prefix}.${id}`, code: 'FORM_WARNING', warning_id: declared.warning_id, message: warningText(declared.message, declared.warning_id, { value: duplicateKey(group.values[id]) }) });
      }
    }
  }
  return warnings;
}

/** Messages par chemin de réponse, pour les afficher sous leur champ. */
export function warningsByPath(warnings: readonly FormWarning[]): Readonly<Record<string, string[]>> {
  const out: Record<string, string[]> = {};
  for (const warning of warnings) (out[warning.field] ??= []).push(warning.message);
  return out;
}

/** Le gabarit déclare-t-il un doublon « déjà enregistré », que seul le serveur peut chercher ? */
export function needsServerWarnings(template: InspectionTemplateBody): boolean {
  return template.sections.some((section) => section.fields.some((entry) => (entry as { duplicates?: { existing?: unknown } }).duplicates?.existing !== undefined));
}
